package com.bulut.mecitogullari.ui.screens.purchase

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Add
import androidx.compose.material.icons.filled.ArrowBack
import androidx.compose.material.icons.filled.Close
import androidx.compose.material.icons.filled.Delete
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Text
import androidx.compose.material3.TopAppBar
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.unit.dp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import com.bulut.mecitogullari.core.i18n.LocalStrings
import com.bulut.mecitogullari.core.i18n.fillTemplate
import com.bulut.mecitogullari.core.util.formatAmountPrecise
import com.bulut.mecitogullari.data.model.CommonUnits
import com.bulut.mecitogullari.ui.components.DateField
import com.bulut.mecitogullari.ui.components.DropdownField
import com.bulut.mecitogullari.ui.theme.AppColors
import org.koin.compose.viewmodel.koinViewModel
import org.koin.core.parameter.parametersOf

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun PurchaseScreen(
    supplierId: String,
    entryId: String,
    onClose: () -> Unit,
    viewModel: PurchaseViewModel = koinViewModel { parametersOf(supplierId, entryId) },
) {
    val uiState by viewModel.uiState.collectAsStateWithLifecycle()
    val strings = LocalStrings.current

    LaunchedEffect(uiState.closed) {
        if (uiState.closed) {
            viewModel.onClosedHandled()
            onClose()
        }
    }

    Scaffold(
        containerColor = AppColors.Background,
        topBar = {
            TopAppBar(
                title = {
                    Text(
                        when {
                            uiState.isEditing && uiState.isCustomer -> strings.editSale
                            uiState.isEditing -> strings.editPurchase
                            uiState.isCustomer -> strings.materialSale
                            else -> strings.materialPurchase
                        }
                    )
                },
                navigationIcon = {
                    IconButton(onClick = onClose) {
                        Icon(Icons.Default.ArrowBack, contentDescription = strings.back)
                    }
                },
                actions = {
                    if (uiState.isEditing) {
                        IconButton(onClick = viewModel::delete) {
                            Icon(Icons.Default.Delete, contentDescription = "Sil", tint = AppColors.Expense)
                        }
                    }
                },
            )
        },
    ) { padding ->
        Column(
            modifier = Modifier
                .fillMaxSize()
                .padding(padding)
                .verticalScroll(rememberScrollState())
                .padding(16.dp),
            verticalArrangement = Arrangement.spacedBy(12.dp),
        ) {
            DateField(
                label = "Tarih",
                dateEpochMillis = uiState.dateEpochMillis,
                onDateSelected = viewModel::onDateChange,
                modifier = Modifier.fillMaxWidth(),
            )

            OutlinedTextField(
                value = uiState.receiver,
                onValueChange = viewModel::onReceiverChange,
                label = { Text("Teslim Alan") },
                singleLine = true,
                shape = RoundedCornerShape(12.dp),
                modifier = Modifier.fillMaxWidth(),
            )

            Text(
                text = "Malzemeler",
                style = MaterialTheme.typography.titleSmall,
                modifier = Modifier.padding(top = 4.dp),
            )

            uiState.items.forEachIndexed { index, item ->
                ItemCard(
                    index = index,
                    item = item,
                    showRemove = uiState.items.size > 1,
                    onChange = { viewModel.onItemChange(index, it) },
                    onRemove = { viewModel.removeItem(index) },
                )
            }

            OutlinedButton(
                onClick = viewModel::addItem,
                shape = RoundedCornerShape(12.dp),
                modifier = Modifier.fillMaxWidth(),
            ) {
                Icon(Icons.Default.Add, contentDescription = null)
                Text("Malzeme Ekle", modifier = Modifier.padding(start = 6.dp))
            }

            Row(
                modifier = Modifier.fillMaxWidth().padding(top = 4.dp),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Text("Genel Toplam", style = MaterialTheme.typography.titleMedium)
                Text(
                    text = formatAmountPrecise(uiState.total),
                    style = MaterialTheme.typography.titleMedium,
                    color = if (uiState.isCustomer) AppColors.Income else AppColors.Expense,
                )
            }

            uiState.error?.let { error ->
                Text(
                    text = error,
                    color = AppColors.Expense,
                    style = MaterialTheme.typography.bodySmall,
                )
            }

            Button(
                onClick = viewModel::save,
                enabled = !uiState.isSaving,
                shape = RoundedCornerShape(12.dp),
                colors = ButtonDefaults.buttonColors(containerColor = AppColors.Navy),
                modifier = Modifier.fillMaxWidth().height(52.dp),
            ) {
                Text("Kaydet", style = MaterialTheme.typography.titleMedium, color = Color.White)
            }
        }
    }
}

@Composable
private fun ItemCard(
    index: Int,
    item: ItemDraft,
    showRemove: Boolean,
    onChange: (ItemDraft) -> Unit,
    onRemove: () -> Unit,
) {
    val strings = LocalStrings.current
    Card(
        shape = RoundedCornerShape(14.dp),
        colors = CardDefaults.cardColors(containerColor = Color.White),
        elevation = CardDefaults.cardElevation(defaultElevation = 1.dp),
    ) {
        Column(modifier = Modifier.padding(12.dp), verticalArrangement = Arrangement.spacedBy(8.dp)) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                OutlinedTextField(
                    value = item.name,
                    onValueChange = { onChange(item.copy(name = it)) },
                    label = { Text("Malzeme ${index + 1}") },
                    singleLine = true,
                    shape = RoundedCornerShape(10.dp),
                    modifier = Modifier.weight(1f),
                )
                if (showRemove) {
                    IconButton(onClick = onRemove) {
                        Icon(Icons.Default.Close, contentDescription = strings.deleteRow, tint = AppColors.TextSecondary)
                    }
                }
            }
            Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                OutlinedTextField(
                    value = item.quantityText,
                    onValueChange = { onChange(item.copy(quantityText = it)) },
                    label = { Text("Miktar") },
                    singleLine = true,
                    keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Decimal),
                    shape = RoundedCornerShape(10.dp),
                    modifier = Modifier.weight(1f),
                )
                DropdownField(
                    label = "Birim",
                    value = item.unit,
                    options = CommonUnits,
                    onSelect = { onChange(item.copy(unit = it)) },
                    modifier = Modifier.weight(1f),
                )
                OutlinedTextField(
                    value = item.unitPriceText,
                    onValueChange = { onChange(item.copy(unitPriceText = it)) },
                    label = { Text("Birim ₺") },
                    singleLine = true,
                    keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Decimal),
                    shape = RoundedCornerShape(10.dp),
                    modifier = Modifier.weight(1f),
                )
            }
            if (item.total > 0) {
                Text(
                    text = strings.rowTotal.fillTemplate("amount" to formatAmountPrecise(item.total)),
                    style = MaterialTheme.typography.bodySmall,
                    color = AppColors.TextSecondary,
                    fontWeight = FontWeight.Medium,
                    modifier = Modifier.align(Alignment.End),
                )
            }
        }
    }
}
